import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppointmentStatus, IAppointment } from '@core/interfaces';
import { AppointmentService, BusinessService } from '@core/services';
import { extractApiErrorMessages } from '@app/utilities';
import { catchError, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';

/**
 * Public page behind the "manage booking" link in booking emails. The token in the URL
 * identifies the booking, so guests can see it and cancel it without an account.
 */
@Component({
  templateUrl: './manage-booking.component.html',
  styleUrls: ['./manage-booking.component.css']
})
export class ManageBookingComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  private token = '';

  public isLoading = true;
  public notFound = false;
  public booking: IAppointment | null = null;
  public business: any = null;

  public cancellationReason = '';
  public isCanceling = false;
  public cancelErrors: string[] = [];
  public justCanceled = false;

  constructor(
    private route: ActivatedRoute,
    private appointmentService: AppointmentService,
    private businessService: BusinessService
  ) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap(params => {
          this.token = params.get('token') ?? '';
          this.isLoading = true;
          return this.appointmentService.getByToken(this.token).pipe(catchError(() => of(null)));
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(booking => {
        this.booking = booking;
        this.notFound = !booking;
        this.isLoading = false;
        if (booking?.businessProfileId) {
          this.businessService.getBusinessProfile(booking.businessProfileId)
            .pipe(catchError(() => of(null)), takeUntil(this.unsubscribe$))
            .subscribe(business => this.business = business);
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public get serviceNames(): string {
    return (this.booking?.services ?? []).map(s => s.name).join(', ');
  }

  public get canCancel(): boolean {
    const status = this.booking?.status;
    return !!this.booking
      && (status === AppointmentStatus.Pending || status === AppointmentStatus.Confirmed)
      && new Date(this.booking.startDate) > new Date();
  }

  public cancel(): void {
    if (!this.canCancel || this.isCanceling) {
      return;
    }

    this.isCanceling = true;
    this.cancelErrors = [];
    this.appointmentService.cancelByToken(this.token, this.cancellationReason.trim() || null)
      .pipe(finalize(() => this.isCanceling = false), takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.booking = { ...this.booking, status: AppointmentStatus.Canceled };
          this.justCanceled = true;
        },
        error: error => {
          const messages = extractApiErrorMessages(error);
          this.cancelErrors = messages.length ? messages : ['manage_booking.cancel_failed'];
        }
      });
  }
}
